// Shared feature-flag catalog for the Locations tab (per-store settings) and
// the Company Settings tab (company-wide flag management). The backend's
// canonical flag list is Features.get_all_features(); this module carries the
// presentation metadata (labels, descriptions, grouping, lock messaging).

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PunchClockIcon from "@mui/icons-material/PunchClock";
import ChatIcon from "@mui/icons-material/Chat";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import FolderIcon from "@mui/icons-material/Folder";
import HandymanIcon from "@mui/icons-material/Handyman";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

export const ALWAYS_ENABLED_FLAGS = ["mobile_app_enabled"];

export const FEATURE_CATEGORIES = {
  "Scheduling, Forecasting & Labor": [
    "scheduling_enabled",
    "labor_model_enabled",
    "live_labor_enabled",
    "kiosk_task_pin_required",
    "published_schedule_enabled",
    "forecasting_enabled",
    "copilot_enabled",
    "labor_law_enabled",
    "block_publish_on_labor_violations_enabled",
    "publish_schedule_review_required_enabled",
    "paid_break_inputs_enabled",
    "manual_sales_input_enabled",
    "tplh_enabled",
    "labor_percent_enabled",
    "lock_forecast_on_publish_enabled",
    "print_hours_shown_enabled",
    "breaks_shown_to_employee_enabled",
    "business_decline_shift_option_enabled",
    "edit_shifts_in_past_enabled",
    "break_waiver_enabled"
  ],
  "Team Management & Communication": [
    "employees_enabled",
    "chat_enabled",
    "daily_working_chats_enabled",
    "approvals_enabled",
    "contests_enabled",
    "check_ins_enabled",
    "employee_create_chat_enabled",
    "manager_create_chat_enabled",
    "store_admins_in_store_chat_enabled"
  ],
  "Reporting & Analytics": [
    "overview_enabled",
    "reporting_enabled",
    "ai_reporting_enabled",
    "dashboard_enabled",
    "guest_reviews_enabled",
    "labor_recap_emails_enabled"
  ],
  "Time & Payroll": [
    "time_entries_enabled",
    "tip_calculator_enabled",
    "tip_pooling_enabled",
    "time_entries_on_mobile_enabled",
    "time_entries_geofencing_enabled",
    "payroll_employee_review_enabled",
    "time_entries_hours_rounded_to_tenths_enabled",
    "payroll_enabled",
    "payroll_wages_enabled",
    "wages_in_rightwork_enabled",
    "time_entries_role_selection_enabled",
    "time_entries_auto_approval_enabled",
    "block_manager_self_timecard_edit_enabled"
  ],
  "Training & Company Files": [
    "training_enabled",
    "proficiency_from_lms_enabled",
    "engagement_enabled",
    "documents_enabled",
    "assistant_enabled"
  ],
  "Operations Execution & Planning": [
    "daily_plan_enabled",
    "boards_enabled",
    "logbook_enabled",
    "maintenance_enabled",
    "it_tickets_enabled",
    "marketing_tickets_enabled",
    "menu_tickets_enabled",
    "deployment_enabled",
    "headcount_planning_enabled",
    "operations_assistant_enabled",
    "prep_plan_enabled"
  ]
};

export const ORDERED_FLAGS = Object.values(FEATURE_CATEGORIES).flat();

// Features that control primary navigation modules
export const MAJOR_TAB_FEATURES = [
  "scheduling_enabled",
  "chat_enabled",
  "daily_plan_enabled",
  "boards_enabled",
  "logbook_enabled",
  "approvals_enabled",
  "published_schedule_enabled",
  "payroll_enabled",
  "time_entries_enabled",
  "training_enabled",
  "engagement_enabled",
  "documents_enabled",
  "overview_enabled",
  "deployment_enabled",
  "maintenance_enabled",
  "it_tickets_enabled",
  "marketing_tickets_enabled",
  "menu_tickets_enabled",
  "contests_enabled",
  "guest_reviews_enabled",
  "reporting_enabled",
  "dashboard_enabled",
  "employees_enabled",
  "labor_model_enabled",
  "headcount_planning_enabled",
  "live_labor_enabled",
  "check_ins_enabled",
  "tip_pooling_enabled",
  "prep_plan_enabled"
];

// Strong capability add-ons that are meaningful, but not primary navigation modules
export const ADDITIONAL_FUNCTIONALITY_FLAGS = [
  "daily_working_chats_enabled",
  "time_entries_on_mobile_enabled",
  "time_entries_geofencing_enabled",
  "tip_calculator_enabled",
  "payroll_employee_review_enabled",
  "forecasting_enabled",
  "labor_recap_emails_enabled",
  "labor_law_enabled",
  "copilot_enabled",
  "assistant_enabled",
  "ai_reporting_enabled",
  "operations_assistant_enabled",
  "kiosk_task_pin_required",
];

export const FEATURE_GROUPS = [
  {
    key: "major_tabs",
    label: "Major tabs",
    description: "Primary navigation modules that materially change the product surface area."
  },
  {
    key: "additional_functionality",
    label: "Additional functionality",
    description: "Meaningful capabilities and workflows that extend each module."
  },
  {
    key: "preferences",
    label: "Preferences",
    description: "Defaults, display choices, and policy-style settings."
  }
];

export const FEATURE_LABELS = {
  scheduling_enabled: "Scheduling",
  daily_plan_enabled: "Task Management",
  payroll_enabled: "Time Entries Module",
  time_entries_enabled: "Rightwork Time Clock",
  time_entries_on_mobile_enabled: "Time Clock on Mobile",
  time_entries_geofencing_enabled: "Clock-In Geofencing",
  time_entries_role_selection_enabled: "Clock-In Role Selection",
  time_entries_auto_approval_enabled: "Auto-Approve Time Entries",
  publish_schedule_review_required_enabled: "Publish Schedule Review",
  block_publish_on_labor_violations_enabled: "Block Publish on Violations",
  time_entries_hours_rounded_to_tenths_enabled: "Round Hours to Tenths",
  payroll_employee_review_enabled: "Employee Payroll Review",
  payroll_wages_enabled: "Show Wages in Time Entries",
  documents_enabled: "Company Files",
  assistant_enabled: "Chat with Company Files",
  mobile_app_enabled: "Mobile App",
  ai_reporting_enabled: "AI Insights",
  engagement_enabled: "Engagement",
  it_tickets_enabled: "IT Tickets",
  menu_tickets_enabled: "Menu Tickets",
  check_ins_enabled: "Check-Ins",
  block_manager_self_timecard_edit_enabled: "Block Manager Self-Edits",
  live_labor_enabled: "Live Action",
  kiosk_task_pin_required: "Require PIN on task kiosk",
  tip_pooling_enabled: "Tip Pooling",
  prep_plan_enabled: "Prep Plan",
  break_waiver_enabled: "Meal Period Waiver",
};

export const FEATURE_TOOLTIPS = {
  scheduling_enabled: "Master switch for all scheduling features. When off, the schedule and all scheduling related views are hidden.",
  time_entries_enabled: "Enables Rightwork clock-in and clock-out so this store captures time directly in Rightwork.",
  time_entries_geofencing_enabled: "Requires employee clock-ins within 100 meters of this location. Managers, admins and shared kiosks are exempt. Enable only after updating the mobile app and verifying the location pin.",
  time_entries_on_mobile_enabled: "Allows employees to clock in and out from the Rightwork mobile app.",
  time_entries_role_selection_enabled: "Prompts employees to choose a role when they clock in.",
  time_entries_auto_approval_enabled: "Automatically approves submitted time entries when they meet your approval rules.",
  time_entries_hours_rounded_to_tenths_enabled: "Rounds recorded hours to the nearest tenth.",
  payroll_enabled: "Allows users to open Time Entries, review and edit entries, and run payroll exports.",
  payroll_wages_enabled: "Shows wages earned in Time Entries reporting instead of dollars only.",
  wages_in_rightwork_enabled: "Stores and manages wages directly in Rightwork.",
  payroll_employee_review_enabled: "Enables employees to review payroll details before submission.",
  tip_calculator_enabled: "Enables the tip calculator to be used within time entries.",
  tip_pooling_enabled: "Enables tip pooling: policies assigned to locations from the Tip pooling row's Tip pool policies page, with the weekly close, breakdown, and payroll file under Time & Payroll > Tips.",
  forecasting_enabled: "Enables sales and labor forecasting tools.",
  manual_sales_input_enabled: "Uses manual sales entry instead of automatic POS imports.",
  labor_recap_emails_enabled: "Sends labor recap emails for this store.",
  labor_model_enabled: "Turns on labor model planning tab.",
  labor_law_enabled: "Applies labor law guidance and checks.",
  tplh_enabled: "Overrides Sales per Labor Hour to Transactions for productivity metrics.",
  labor_percent_enabled: "Shows labor percentage metrics when wages are available.",
  lock_forecast_on_publish_enabled: "Locks forecasts when a schedule is published.",
  publish_schedule_review_required_enabled: "Requires a review before schedules can be published.",
  block_publish_on_labor_violations_enabled: "Prevents publishing a schedule that violates meal break, rest break, clopen, or minor work-hour rules.",
  print_hours_shown_enabled: "Includes hours when printing schedules.",
  paid_break_inputs_enabled: "Lets managers schedule paid breaks with shifts.",
  breaks_shown_to_employee_enabled: "Displays the specific time of scheduled breaks to employees.",
  business_decline_shift_option_enabled: "Lets managers mark a shift as Business Decline (BD), hiding the end time from employees.",
  copilot_enabled: "Enables Copilot-assisted scheduling and labor tools.",
  edit_shifts_in_past_enabled: "Allows edits to shifts that have already started or ended.",
  headcount_planning_enabled: "Plan staffing levels across locations and compare your current team with the headcount you need.",
  break_waiver_enabled: "Allows meal period waivers to be added for employees to waive meal periods.",
  live_labor_enabled: "Shows the live-day labor view (the Live Action tab) with real-time labor tracking and floor alerts.",
  kiosk_task_pin_required:
    "On the shared-device Live Action kiosk, people enter their time clock PIN instead of picking their name. Everyone who works tasks from the kiosk needs a PIN on their profile.",
  daily_plan_enabled: "Enables daily task assignment workflows.",
  boards_enabled: "Enables the Boards kanban for planning projects and tasks.",
  logbook_enabled: "Enables the logbook tab and entries.",
  approvals_enabled: "Enables approval workflows for availability requests and schedule changes.",
  published_schedule_enabled: "Enables the published schedule view.",
  reporting_enabled: "Enables store and employee self service reporting tabs.",
  ai_reporting_enabled: "Enables the AI Insights chat inside Store Analytics and Employee Analytics.",
  dashboard_enabled: "Enables the dashboard tab with saved reports and templates.",
  maintenance_enabled: "Enables maintenance issue tracking.",
  prep_plan_enabled: "Shows managers at this location how much to prep from the sales forecast, under Inventory.",
  it_tickets_enabled: "Enables IT ticket tracking.",
  marketing_tickets_enabled: "Enables marketing ticket tracking.",
  menu_tickets_enabled: "Enables menu change request tracking.",
  training_enabled: "Enables learning management system with training content and assignments.",
  engagement_enabled: "Enables the Engagement tab: surveys, polls and swipe decks shared by public link or QR code.",
  proficiency_from_lms_enabled: "Uses proficiency from the learning management system in schedule view.",
  overview_enabled: "Enables the overview tab with weekly store summary grades.",
  documents_enabled: "Enables company files and file access.",
  assistant_enabled: "Enables the Rightwork assistant for questions on company files.",
  employees_enabled: "Enables employee roster and profile management.",
  chat_enabled: "Enables store chat and messaging.",
  daily_working_chats_enabled: "Enables the automatic daily working chats for employees scheduled in this store.",
  employee_create_chat_enabled: "Allows employees to start new chats.",
  manager_create_chat_enabled: "Allows managers to start new chats.",
  store_admins_in_store_chat_enabled: "Automatically includes store admins in store chats.",
  contests_enabled: "Enables contests and incentive tracking tab.",
  check_ins_enabled: "Enables structured manager 1-on-1 check-ins on the employee profile, plus the shared mobile read view for employees.",
  guest_reviews_enabled: "Enables guest review monitoring tab.",
  deployment_enabled: "Organize your team across zones and positions so managers can see where people are assigned during a shift.",
  mobile_app_enabled: "The Rightwork mobile app for your team. Always enabled.",
  block_manager_self_timecard_edit_enabled: "Prevents managers from editing, deleting, or creating their own timecards. Admins can still edit their own timecards."
};

export const LOCKED_FLAG_MESSAGES = {
  contests_enabled: "Reach out to your Rightwork representative to configure this setting.",
  deployment_enabled: "Reach out to your Rightwork representative to configure this setting.",
  guest_reviews_enabled: "Reach out to your Rightwork representative to configure this setting.",
  headcount_planning_enabled: "Reach out to your Rightwork representative to configure this setting.",
  prep_plan_enabled: "Reach out to your Rightwork representative to configure this setting."
};

// Presentation metadata only. The backend still controls who can change flags;
// a badge never grants company access or represents a purchased entitlement.
export const FEATURE_ACCESS = {
  deployment_enabled: { kind: "beta", label: "Beta", message: "Deployment is in beta. Contact your Rightwork representative to discuss access for your company." },
  headcount_planning_enabled: { kind: "beta", label: "Beta", message: "Headcount Planning is in beta. Contact your Rightwork representative to discuss access for your company." },
  contests_enabled: { kind: "managed", label: "Managed", message: LOCKED_FLAG_MESSAGES.contests_enabled },
  guest_reviews_enabled: { kind: "managed", label: "Managed", message: LOCKED_FLAG_MESSAGES.guest_reviews_enabled },
};

export const formatFlagName = (flag) =>
  FEATURE_LABELS[flag] ||
  flag
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(" Enabled", "");

export const getFlagTooltip = (flag) =>
  FEATURE_TOOLTIPS[flag] || `${formatFlagName(flag)} setting for this store.`;

export const getFeatureGroupKey = (flag) => {
  if (MAJOR_TAB_FEATURES.includes(flag)) {
    return "major_tabs";
  }
  if (ADDITIONAL_FUNCTIONALITY_FLAGS.includes(flag)) {
    return "additional_functionality";
  }
  return "preferences";
};

export const getCategoryForFlag = (flag) =>
  Object.keys(FEATURE_CATEGORIES).find((category) =>
    FEATURE_CATEGORIES[category].includes(flag)
  );

// Product-module buckets for the Company Settings tab: each card is a module
// with one primary flag (the card's on/off identity) and child flags that
// only matter when the primary is on (or that share the module's surface).
// Independent major features get their own bucket — a child slot implies a
// dependency, so don't nest peers (e.g. Boards is NOT under Task Management).
export const FEATURE_BUCKETS = [
  {
    key: "scheduling",
    title: "Scheduling",
    tagline: "Build and publish schedules in minutes",
    primaryFlag: "scheduling_enabled",
    childFlags: [
      "published_schedule_enabled",
      "approvals_enabled",
      "copilot_enabled",
      "publish_schedule_review_required_enabled",
      "edit_shifts_in_past_enabled",
      "breaks_shown_to_employee_enabled",
      "business_decline_shift_option_enabled",
      "paid_break_inputs_enabled",
      "print_hours_shown_enabled",
    ],
  },
  {
    key: "forecasting",
    title: "Forecasting",
    tagline: "Predict demand using real-time POS data",
    primaryFlag: "forecasting_enabled",
    requiresScheduling: true,
    childFlags: [
      "manual_sales_input_enabled",
      "lock_forecast_on_publish_enabled",
      "tplh_enabled",
      "labor_percent_enabled",
    ],
  },
  {
    key: "labor_modeling",
    title: "Labor Modeling",
    tagline: "Turn sales into the perfect staffing plan",
    primaryFlag: "labor_model_enabled",
    requiresScheduling: true,
    childFlags: [],
  },
  {
    // Its own bucket, not a Time & Attendance option: pooling runs on
    // Toast orders and punches alone, so neither Payroll nor Scheduling
    // being off pauses it, and the page must not say it does.
    key: "tip_pooling",
    title: "Tip pooling",
    tagline: "Pool Toast tips and service charges by policy, close weekly, export to payroll",
    primaryFlag: "tip_pooling_enabled",
    childFlags: [],
    internalOnly: true,
  },
  {
    key: "time_attendance",
    title: "Time & Attendance",
    tagline: "Accurate timecards, painless payroll and tip exports",
    primaryFlag: "payroll_enabled",
    requiresScheduling: true,
    childFlags: [
      "time_entries_enabled",
      "time_entries_on_mobile_enabled",
      "time_entries_geofencing_enabled",
      "time_entries_role_selection_enabled",
      "time_entries_auto_approval_enabled",
      "time_entries_hours_rounded_to_tenths_enabled",
      "tip_calculator_enabled",
      "payroll_wages_enabled",
      "wages_in_rightwork_enabled",
      "payroll_employee_review_enabled",
      "block_manager_self_timecard_edit_enabled",
    ],
    // Config panels (not flags), expanded inline on a location row and on the
    // Company Default card. `companyDescription` is used on the latter, where
    // "this location" would be wrong.
    //
    // `isEnabled(flags, masterOn)` decides reachability. It must match what the
    // panel actually renders: a company default is pushed per surface, so a
    // surface that opens on a flag its panel ignores would push settings the
    // admin never saw. `section` is the panel's own view name when it differs
    // from the surface key.
    configSurfaces: [
      {
        key: "clock",
        label: "Time Clock",
        description:
          "Clock-in requirements, breaks, and auto clock-out for this location.",
        companyDescription:
          "Clock-in requirements, breaks, and auto clock-out new locations start with.",
        // The Rightwork clock, not POS time entries. payroll_enabled alone must
        // NOT open this: with time_entries_enabled off the panel shows only the
        // POS row, which is the `posTimeEntries` surface below.
        isEnabled: (flags) => Boolean(flags.time_entries_enabled),
        // See posTimeEntries: these two are the modes of one panel, selected by
        // the "Rightwork Time Clock" toggle in this same bucket. Never show the
        // inactive one.
        hideWhenDisabled: true,
      },
      {
        key: "posTimeEntries",
        section: "clock",
        label: "Time Entries",
        description:
          "How POS-sourced time entries are displayed at this location.",
        companyDescription:
          "How POS-sourced time entries are displayed at new locations.",
        isEnabled: (flags, masterOn) =>
          masterOn && !flags.time_entries_enabled,
        // Time Clock's POS-mode alternative, not a capability to switch on. A
        // disabled surface normally greys out to say "enable something to unlock
        // this", but the only way to reach this one is to turn the Rightwork
        // clock OFF. Exactly one of the two ever applies, so hide the other.
        hideWhenDisabled: true,
      },
      {
        key: "flagging",
        label: "Time Entry Flagging",
        description:
          "Which timecard exceptions get flagged for review at this location.",
        companyDescription:
          "Which timecard exceptions new locations start out flagging for review.",
        // Flagging runs over time entries from either source, so payroll alone
        // is enough. The panel renders identically in both modes.
        isEnabled: (flags, masterOn) =>
          masterOn || Boolean(flags.time_entries_enabled),
      },
    ],
  },
  {
    key: "labor_compliance",
    title: "Labor Compliance",
    tagline: "Auto-enforce laws across every jurisdiction",
    primaryFlag: "labor_law_enabled",
    requiresScheduling: true,
    childFlags: [
      "block_publish_on_labor_violations_enabled",
      "break_waiver_enabled",
    ],
  },
  {
    // The live-day labor view (gated by live_labor_enabled, which also shows
    // the "Live Action" nav tab). Houses the per-location alert preferences.
    key: "live_action",
    title: "Live Action",
    tagline: "The live-day view and its real-time alerts",
    primaryFlag: "live_labor_enabled",
    requiresScheduling: true,
    childFlags: ["kiosk_task_pin_required"],
    // Config panel (not a flag), expanded inline on a location row and on the
    // Company Default card.
    configSurfaces: [
      {
        key: "liveAlerts",
        label: "Alert settings",
        description:
          "Real-time alerts for no-shows, break overruns, overtime, and labor vs forecast.",
      },
    ],
  },
  {
    key: "team_chat",
    title: "Team Chat",
    tagline: "Keep your team connected every shift",
    primaryFlag: "chat_enabled",
    childFlags: [
      "daily_working_chats_enabled",
      "employee_create_chat_enabled",
      "manager_create_chat_enabled",
      "store_admins_in_store_chat_enabled",
    ],
  },
  {
    key: "team_management",
    title: "Team Management",
    tagline: "Rosters, profiles, and access in one place",
    primaryFlag: "employees_enabled",
    childFlags: ["check_ins_enabled"],
  },
  {
    key: "reporting",
    title: "Reporting & Analytics",
    tagline: "Real-time visibility across every location",
    primaryFlag: "reporting_enabled",
    requiresScheduling: true,
    childFlags: [
      "overview_enabled",
      "dashboard_enabled",
      "ai_reporting_enabled",
      "labor_recap_emails_enabled",
      "guest_reviews_enabled",
    ],
  },
  {
    key: "training",
    title: "Training",
    tagline: "Onboard and upskill with built-in courses",
    primaryFlag: "training_enabled",
    childFlags: ["proficiency_from_lms_enabled"],
  },
  {
    key: "engagement",
    title: "Engagement",
    tagline: "Surveys, polls and quick updates anyone can answer from a link",
    primaryFlag: "engagement_enabled",
    childFlags: [],
  },
  {
    key: "documents",
    title: "Document Management",
    tagline: "Your company files, organized and searchable",
    primaryFlag: "documents_enabled",
    childFlags: ["assistant_enabled"],
  },
  {
    key: "maintenance",
    title: "Maintenance & Ticketing",
    tagline: "Track every issue, resolve it faster",
    primaryFlag: "maintenance_enabled",
    childFlags: ["it_tickets_enabled", "marketing_tickets_enabled", "menu_tickets_enabled"],
  },
  {
    key: "prep_plan",
    title: "Prep plan",
    tagline: "How much to prep for each day, from the sales forecast",
    primaryFlag: "prep_plan_enabled",
    childFlags: [],
    internalOnly: true,
  },
  {
    key: "ai_assistant",
    title: "AI Assistant",
    tagline: "Your AI copilot for daily operations",
    primaryFlag: "operations_assistant_enabled",
    childFlags: [],
  },
  {
    key: "task_management",
    title: "Task Management",
    tagline: "Assign daily tasks and track completion live",
    primaryFlag: "daily_plan_enabled",
    requiresScheduling: true,
    childFlags: [],
  },
  {
    key: "logbook",
    title: "Logbook",
    tagline: "Shift notes and daily store journal",
    primaryFlag: "logbook_enabled",
    childFlags: [],
  },
  {
    key: "contests",
    title: "Contests",
    tagline: "Drive results with live leaderboards and races",
    primaryFlag: "contests_enabled",
    childFlags: [],
  },
  {
    key: "boards",
    title: "Boards",
    tagline: "Plan projects and track work on kanban boards",
    primaryFlag: "boards_enabled",
    childFlags: [],
  },
  // Beta features are discoverable in settings; activation remains restricted
  // to Rightwork by the existing frontend and backend flag permissions.
  {
    key: "deployment",
    title: "Deployment",
    tagline: "Position your team across zones",
    primaryFlag: "deployment_enabled",
    requiresScheduling: true,
    childFlags: [],
  },
  {
    key: "headcount_planning",
    title: "Headcount Planning",
    tagline: "Plan staffing levels across locations",
    primaryFlag: "headcount_planning_enabled",
    requiresScheduling: true,
    childFlags: [],
  },
  {
    key: "mobile_app",
    title: "Mobile App",
    tagline: "Everything your team needs in their pocket",
    primaryFlag: "mobile_app_enabled",
    childFlags: [],
  },
];

export const BUCKET_ICONS = {
  scheduling: CalendarMonthIcon,
  forecasting: QueryStatsIcon,
  labor_modeling: TrendingUpIcon,
  time_attendance: PunchClockIcon,
  labor_compliance: VerifiedUserOutlinedIcon,
  live_action: PlayCircleOutlineIcon,
  team_chat: ChatIcon,
  team_management: PeopleIcon,
  task_management: ChecklistRoundedIcon,
  boards: ViewKanbanIcon,
  reporting: BarChartIcon,
  documents: FolderIcon,
  ai_assistant: AutoAwesomeIcon,
  training: SchoolIcon,
  engagement: CampaignOutlinedIcon,
  maintenance: HandymanIcon,
  logbook: MenuBookIcon,
  contests: EmojiEventsIcon,
  deployment: GroupsIcon,
  headcount_planning: InsightsIcon,
  mobile_app: SmartphoneIcon,
};

export const getBucketForFlag = (flag) =>
  FEATURE_BUCKETS.find(
    (bucket) => bucket.primaryFlag === flag || bucket.childFlags.includes(flag)
  );

// Buckets visible to the given role: internalOnly buckets render for
// rightwork_internal users only.
export const getVisibleBuckets = (isRightworkInternal) =>
  FEATURE_BUCKETS.filter((bucket) => !bucket.internalOnly || isRightworkInternal);

// Completeness guard: every flag in the catalog (plus always-on flags) must
// live in exactly one bucket, so a newly added flag can't silently vanish
// from the Company Settings UI or appear on two cards.
(() => {
  const seen = new Set();
  const duplicates = [];
  FEATURE_BUCKETS.forEach((bucket) => {
    [bucket.primaryFlag, ...bucket.childFlags].forEach((flag) => {
      if (seen.has(flag)) duplicates.push(flag);
      seen.add(flag);
    });
  });
  const expected = new Set([...ORDERED_FLAGS, ...ALWAYS_ENABLED_FLAGS]);
  const missing = [...expected].filter((flag) => !seen.has(flag));
  const unknown = [...seen].filter((flag) => !expected.has(flag));
  if (missing.length || unknown.length || duplicates.length) {
    // eslint-disable-next-line no-console
    console.error(
      "FEATURE_BUCKETS out of sync with the flag catalog:",
      JSON.stringify({ missing, unknown, duplicates })
    );
  }
})();
